import React, { useState, useEffect } from 'react';
import { templatesAPI } from '../services/api';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import SearchInput from '../components/SearchInput';
import { TableSkeleton } from '../components/LoadingSpinner';
import { useToast } from '../hooks/useToast';
import { TemplatesIcon } from '../components/Icons';
import './Templates.css';

export default function Templates() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    body: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = templates.filter(template =>
        template.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.body?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTemplates(filtered);
    } else {
      setFilteredTemplates(templates);
    }
  }, [searchQuery, templates]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templatesAPI.getAll();
      // Handle paginated response
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setTemplates(data);
      setFilteredTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      if (error.response?.status === 403) {
        toast.warning('Authentication required. Please login.');
      } else {
        toast.error('Failed to load templates. Check console for details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.body.trim()) errors.body = 'Body is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix form errors');
      return;
    }

    try {
      setActionLoading(true);
      if (editingTemplate) {
        await templatesAPI.update(editingTemplate.id, formData);
        toast.success('Template updated successfully');
      } else {
        await templatesAPI.create(formData);
        toast.success('Template created successfully');
      }
      
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ subject: '', body: '' });
      setFormErrors({});
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template. Check console for details.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      subject: template.subject || '',
      body: template.body || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(id);
      await templatesAPI.delete(id);
      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template. Check console for details.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNew = () => {
    setEditingTemplate(null);
    setFormData({ subject: '', body: '' });
    setFormErrors({});
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({ subject: '', body: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Message Templates</h1>
        </div>
        <Card>
          <TableSkeleton rows={5} cols={4} />
        </Card>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Message Templates</h1>
          <p className="page-subtitle">Create and manage message templates for your campaigns</p>
        </div>
        <div className="page-actions">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            style={{ maxWidth: '300px' }}
          />
          <Button variant="primary" onClick={handleNew}>
            + New Template
          </Button>
        </div>
      </div>

      {filteredTemplates.length === 0 ? (
        <Card>
          <div className="empty-state">
            <div className="empty-state-icon">
              <TemplatesIcon size={48} style={{ opacity: 0.5 }} />
            </div>
            <div className="empty-state-title">
              {searchQuery ? 'No templates found' : 'No templates yet'}
            </div>
            <div className="empty-state-description">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Create your first template to get started with automated outreach'}
            </div>
            {!searchQuery && (
              <Button variant="primary" onClick={handleNew} style={{ marginTop: 'var(--spacing-lg)' }}>
                Create Template
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Body Preview</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map(template => (
                  <tr key={template.id}>
                    <td>
                      <div className="template-subject">
                        {template.subject || <span className="text-secondary">No subject</span>}
                      </div>
                    </td>
                    <td>
                      <div className="template-preview">
                        {template.body
                          ? `${template.body.substring(0, 60)}${template.body.length > 60 ? '...' : ''}`
                          : <span className="text-secondary">No body</span>}
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleEdit(template)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(template.id)}
                          loading={actionLoading === template.id}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal show={showModal} onClose={handleClose} title={editingTemplate ? 'Edit Template' : 'New Template'} width="600px">
        <form onSubmit={handleSubmit}>
          <Input
            label="Subject"
            type="text"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="Email subject or SMS title"
            helperText="Optional for SMS and social media"
          />

          <Input
            label="Body"
            type="textarea"
            value={formData.body}
            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            required
            rows={8}
            placeholder="Message body..."
            error={formErrors.body}
            helperText="This is the main message content"
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-xl)' }}>
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={actionLoading}>
              {editingTemplate ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
